import { c as _c } from "react/compiler-runtime";
import { IcoCheckCircle16, IcoChevronDown16, IcoChevronRight16, IcoRobot16, IcoWarning16 } from '@onefootprint/icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import useDocumentsPanelOpen from '../../hooks/use-documents-panel-open';
import type { JourneyBatchSegment, JourneyGroupSegment } from '../../utils/chat-segments';
import { isErrorTaskStatus, isTerminalTaskStatus, journeyDisplay, journeyGroupStatus } from '../../utils/journey-attribution';
const BatchRow = t0 => {
  const $ = _c(17);
  const {
    group,
    onOpen
  } = t0;
  let isDone;
  let status;
  let t1;
  if ($[0] !== group.messages) {
    status = journeyGroupStatus(group.messages);
    isDone = isTerminalTaskStatus(status);
    t1 = isErrorTaskStatus(status);
    $[0] = group.messages;
    $[1] = isDone;
    $[2] = status;
    $[3] = t1;
  } else {
    isDone = $[1];
    status = $[2];
    t1 = $[3];
  }
  const isError = t1;
  let t2;
  if ($[4] !== group.agent) {
    t2 = journeyDisplay(group.agent);
    $[4] = group.agent;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    label
  } = t2;
  let t3;
  if ($[6] !== isDone || $[7] !== isError || $[8] !== status) {
    t3 = <span className="shrink-0 flex items-center justify-center size-6">{isDone ? isError ? <IcoWarning16 color="error" /> : <IcoCheckCircle16 className="text-primary" /> : <span role="img" aria-label={status} className="size-1.5 rounded-full bg-accent" />}</span>;
    $[6] = isDone;
    $[7] = isError;
    $[8] = status;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== label) {
    t4 = <span className="flex-1 min-w-0 py-1.5 text-label-3 text-primary truncate">{label}</span>;
    $[10] = label;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <span className="shrink-0 size-7 flex items-center justify-center text-quaternary group-hover/jr:text-tertiary"><IcoChevronRight16 /></span>;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== onOpen || $[14] !== t3 || $[15] !== t4) {
    t6 = <button type="button" onClick={onOpen} className="group/jr flex items-center gap-1 w-full pl-8 pr-1.5 text-left cursor-pointer hover:bg-hover transition-colors">{t3}{t4}{t5}</button>;
    $[13] = onOpen;
    $[14] = t3;
    $[15] = t4;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
};

// ≥2 sub-agents spawned together: a collapsible header over the rows, each opening its transcript.
type JourneyBatchCardProps = {
  batch: JourneyBatchSegment;
  /** Opens a sub-agent's transcript; defaults to the chat widget's agents sidebar. */
  onOpenAgent?: (spawnId: string) => void;
};
const JourneyBatchCard = t0 => {
  const $ = _c(22);
  const {
    batch,
    onOpenAgent
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "chat.backgroundAgents"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("copilot", t1);
  const {
    openAgent: openInSidebar
  } = useDocumentsPanelOpen();
  const openAgent = onOpenAgent ?? openInSidebar;
  const [collapsed, setCollapsed] = useState(false);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => setCollapsed(_temp);
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = !collapsed;
  const t4 = `group flex items-center gap-1 w-full pl-2 pr-1.5 text-left cursor-pointer transition-colors ${collapsed ? "hover:bg-hover" : ""}`;
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <span className="shrink-0 flex items-center justify-center size-6 text-primary"><IcoRobot16 /></span>;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  let t6;
  if ($[3] !== batch.groups.length || $[4] !== t) {
    t6 = t(_temp2, {
      count: batch.groups.length
    });
    $[3] = batch.groups.length;
    $[4] = t;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== t6) {
    t7 = <span className="flex-1 min-w-0 py-2 text-label-3 text-primary truncate">{t6}</span>;
    $[6] = t6;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== collapsed) {
    t8 = <span className="shrink-0 size-7 flex items-center justify-center text-quaternary transition group-hover:text-tertiary">{collapsed ? <IcoChevronRight16 /> : <IcoChevronDown16 />}</span>;
    $[8] = collapsed;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== t3 || $[11] !== t4 || $[12] !== t7 || $[13] !== t8) {
    t9 = <button type="button" onClick={t2} aria-expanded={t3} className={t4}>{t5}{t7}{t8}</button>;
    $[10] = t3;
    $[11] = t4;
    $[12] = t7;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== batch.groups || $[16] !== collapsed || $[17] !== openAgent) {
    t10 = !collapsed && <div className="relative flex flex-col"><span aria-hidden={true} className="pointer-events-none absolute left-[19px] top-0 bottom-3 border-l border-solid border-tertiary" />{batch.groups.map(group => <BatchRow key={group.spawnId} group={group} onOpen={() => openAgent(group.spawnId)} />)}</div>;
    $[15] = batch.groups;
    $[16] = collapsed;
    $[17] = openAgent;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t10 || $[20] !== t9) {
    t11 = <div className="flex flex-col rounded-lg border border-solid border-tertiary overflow-hidden">{t9}{t10}</div>;
    $[19] = t10;
    $[20] = t9;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  return t11;
};
export default JourneyBatchCard;
function _temp(c) {
  return !c;
}
function _temp2($) {
  return $.batchTitle;
}